/**
 * The chat box's `/note` and `/todo` quick capture (unit 26B). **Translated
 * from v1's `frontend/src/lib/noteCommand.js`** (`frontend.md` Q3 (a)).
 *
 * **Class B** (`core/parity.py`, `capture_grammar`, `frontend_shell.md` Q1 (a)):
 * capture works with no signal, so the browser parses; the Android app parses
 * the same way since unit 34A, and both read one fixture of input → result,
 * `contract/conformance/capture_grammar.json`.
 *
 * The rule everything here follows: **capture must never lose the thought.** A
 * token not understood stays in the text; nothing is dropped, and no input but
 * an empty one fails to save.
 */

export interface Target {
  id: string | number;
  name: string;
}

export interface Captured {
  title: string;
  body: string;
  tags: string[];
  /** The notebook (`/note`) or the list (`/todo`) it is filed in. */
  target_id: string | number | null;
  target_name: string | null;
  /** An `@word` that named nothing: reported, and left in the text. */
  unknown_target: string | null;
  /** Nothing but tags or a target: the caller refuses it. */
  empty: boolean;
}

/**
 * `#tag`: a hash straight before a word character, at the start or after
 * whitespace. The first half keeps out markdown headings (`# Heading`); the
 * second keeps out a URL's fragment (`example.com/#section`).
 */
const TAG = /(^|\s)#([\p{L}\p{N}][\p{L}\p{N}_-]*)/gu;

/** `@word`, only to report a target that matched nothing; keeps `sam@example.com` out. */
const AT = /(^|\s)@([\p{L}\p{N}][\p{L}\p{N}_-]*)/u;

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Trailing space on each line, and the ends; a line's leading space is list indentation. */
const tidy = (s: string) =>
  s
    .split("\n")
    .map((line) => line.replace(/[ \t]+$/, ""))
    .join("\n")
    .trim();

export function parseCapture(text: string, targets: readonly Target[] = []): Captured {
  let rest = text;

  // One @Target, matched against the names that exist, longest first: names
  // have spaces ("Personal Development"), and nothing else says where one ends.
  let targetId: string | number | null = null;
  let targetName: string | null = null;
  const byLength = [...targets].sort((a, b) => (b.name?.length ?? 0) - (a.name?.length ?? 0));
  for (const target of byLength) {
    if (!target?.name) continue;
    const at = new RegExp(`(^|\\s)@${escape(target.name)}(?=\\s|$)`, "iu");
    if (at.test(rest)) {
      rest = rest.replace(at, "");
      targetId = target.id;
      targetName = target.name;
      break;
    }
  }

  // An @word that matched nothing stays in the text, and is reported.
  let unknown: string | null = null;
  if (targetId === null) unknown = rest.match(AT)?.[2] ?? null;

  // The space before a tag goes with it, so no double space is left behind.
  const tags: string[] = [];
  rest = rest.replace(TAG, (_whole, _lead, tag: string) => {
    if (!tags.some((t) => t.toLowerCase() === tag.toLowerCase())) tags.push(tag);
    return "";
  });

  rest = tidy(rest);
  const [first = "", ...lines] = rest.split("\n");
  return {
    title: first.trim(),
    body: tidy(lines.join("\n")),
    tags,
    target_id: targetId,
    target_name: targetName,
    unknown_target: unknown,
    empty: rest === "",
  };
}
